import {
    EngagementChatSenderType,
    type EngagementChatAttachment,
    type EngagementChatMessageItem,
} from '@houzy/schemas/partner-cockpit/chat';
import type {
    PartnerCockpitContactDetail,
    PartnerCockpitLeadStatus,
    PartnerCockpitOfferRequestContact,
} from '@houzy/schemas/partner-cockpit/offer-requests';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import SendIcon from '@mui/icons-material/Send';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState, type ChangeEvent, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { PostChatMessageInput } from '../api/chatApi';
import { FILL_HEIGHT_CARD_SX } from '../fillHeight';
import { ENGAGEMENT_CHAT_ATTACHMENT_ACCEPT, MAX_ENGAGEMENT_CHAT_ATTACHMENTS } from '../utils/chatAttachmentValidation';
import { isImageAttachment } from '../utils/isImageAttachment';
import { systemNoticeTranslationKey } from '../utils/systemNoticeTranslations';
import { ChatErrorAlert } from './ChatErrorAlert';
import { ChatPendingAttachments } from './ChatPendingAttachments';
import { OfferRequestContactHeader } from './OfferRequestContactCard';

export type OfferRequestChatCardProps = {
    contact?: PartnerCockpitOfferRequestContact;
    leadStatus: PartnerCockpitLeadStatus;
    contactRestricted: boolean;
    onRevealContactDetail: (detail: PartnerCockpitContactDetail) => void;
    messages: EngagementChatMessageItem[];
    isLoading: boolean;
    isLoadError: boolean;
    isSending: boolean;
    isSendError: boolean;
    isUploadingAttachments: boolean;
    uploadError: string | null;
    onDismissUploadError: () => void;
    hasOlderMessages: boolean;
    isLoadingOlderMessages: boolean;
    onLoadOlderMessages: () => void;
    pendingAttachments: EngagementChatAttachment[];
    onUploadFiles: (files: File[]) => void;
    onRemoveAttachment: (attachmentUuid: string) => void;
    onSendMessage: (input: PostChatMessageInput) => Promise<void>;
    isInputDisabled?: boolean;
    disabledNotice?: string;
    isOnOldContract?: boolean;
    fillHeight?: boolean;
};

const MESSAGE_LIST_MAX_HEIGHT = 420;

function formatSentAt(createdAt: string, locale: string): string {
    return new Date(createdAt).toLocaleString(locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
    });
}

const SYSTEM_NOTICE_SEVERITY: Record<NonNullable<EngagementChatMessageItem['systemNotice']>, 'info' | 'success'> = {
    PartnerInterested: 'info',
    PartnerWillContactViaPhoneOrEmail: 'info',
    ContactRequestSent: 'info',
    ContactViaPhoneEmail: 'info',
    ContactRequestAccepted: 'success',
};

const CHAT_STATUS_ALERT_SX = {
    alignSelf: 'center',
    width: '100%',
    maxWidth: 320,
    '& .MuiAlert-message': {
        width: '100%',
        textAlign: 'center',
    },
} as const;

export function ChatStatusAlert({
    severity,
    children,
}: {
    severity: 'info' | 'warning' | 'success';
    children: ReactNode;
}): ReactElement {
    return (
        <Alert severity={severity} variant="standard" icon={false} sx={CHAT_STATUS_ALERT_SX}>
            {children}
        </Alert>
    );
}

function ChatSystemNotice({ message }: { message: EngagementChatMessageItem }): ReactElement | null {
    const { t } = useTranslation();
    const translationKey = systemNoticeTranslationKey(message.systemNotice);
    if (!translationKey || !message.systemNotice) {
        return null;
    }
    // Rendered as plain (React-escaped) text: partner-cockpit system notices carry no markup.
    return (
        <ChatStatusAlert severity={SYSTEM_NOTICE_SEVERITY[message.systemNotice]}>
            {t(translationKey, message.variables ?? {})}
        </ChatStatusAlert>
    );
}

type ChatBubbleVariant = 'author' | 'teammate' | 'other';

const CHAT_BUBBLE_COLORS = {
    author: { background: 'primary.main', text: 'primary.contrastText' },
    teammate: { background: 'Alert.infoStandardBg', text: 'Alert.infoColor' },
    other: { background: 'neutralSurface', text: 'text.primary' },
} satisfies Record<ChatBubbleVariant, { background: string; text: string }>;

function ChatAttachmentFileLink({
    attachment,
    variant,
}: {
    attachment: EngagementChatAttachment;
    variant: ChatBubbleVariant;
}): ReactElement {
    return (
        <Typography
            component="a"
            href={attachment.url}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                color: variant === 'other' ? 'primary.main' : CHAT_BUBBLE_COLORS[variant].text,
                textDecoration: 'underline',
                overflowWrap: 'anywhere',
            }}>
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 16, flexShrink: 0 }} />
            {attachment.displayName}
        </Typography>
    );
}

function ChatAttachmentImage({
    attachment,
    variant,
}: {
    attachment: EngagementChatAttachment;
    variant: ChatBubbleVariant;
}): ReactElement {
    const [hasFailed, setHasFailed] = useState(false);
    if (hasFailed) {
        return <ChatAttachmentFileLink attachment={attachment} variant={variant} />;
    }
    return (
        <Box
            component="a"
            href={attachment.url}
            target="_blank"
            rel="noopener noreferrer"
            title={attachment.displayName}
            sx={{
                display: 'inline-flex',
                maxWidth: '100%',
                borderRadius: 1,
                overflow: 'hidden',
            }}>
            <Box
                component="img"
                src={attachment.url}
                alt={attachment.displayName}
                onError={() => setHasFailed(true)}
                sx={{
                    display: 'block',
                    maxWidth: 240,
                    maxHeight: 240,
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    borderRadius: 1,
                    bgcolor: variant === 'other' ? 'action.hover' : 'filledSurfaceOverlay',
                }}
            />
        </Box>
    );
}

function ChatMessageAttachments({
    attachments,
    variant,
    hasText,
}: {
    attachments: EngagementChatMessageItem['attachments'];
    variant: ChatBubbleVariant;
    hasText: boolean;
}): ReactElement | null {
    if (attachments.length === 0) {
        return null;
    }
    return (
        <Stack spacing={1} sx={{ mt: hasText ? 1 : 0 }}>
            {attachments.map((attachment) =>
                isImageAttachment(attachment) ? (
                    <ChatAttachmentImage key={attachment.uuid} attachment={attachment} variant={variant} />
                ) : (
                    <ChatAttachmentFileLink key={attachment.uuid} attachment={attachment} variant={variant} />
                ),
            )}
        </Stack>
    );
}

function chatBubbleVariant(message: EngagementChatMessageItem): ChatBubbleVariant {
    if (message.sender.isAuthor) {
        return 'author';
    }
    return message.sender.type === EngagementChatSenderType.PartnerCockpitUser ? 'teammate' : 'other';
}

function ChatMessageBubble({ message, locale }: { message: EngagementChatMessageItem; locale: string }): ReactElement {
    const isSentByPartner = message.sender.type === EngagementChatSenderType.PartnerCockpitUser;
    const variant = chatBubbleVariant(message);
    return (
        <Stack alignItems={isSentByPartner ? 'flex-end' : 'flex-start'}>
            <Box
                sx={{
                    maxWidth: '75%',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: CHAT_BUBBLE_COLORS[variant].background,
                    color: CHAT_BUBBLE_COLORS[variant].text,
                }}>
                {message.contentText ? (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                        {message.contentText}
                    </Typography>
                ) : null}
                <ChatMessageAttachments
                    attachments={message.attachments}
                    variant={variant}
                    hasText={message.contentText.length > 0}
                />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {message.sender.name} · {formatSentAt(message.createdAt, locale)}
            </Typography>
        </Stack>
    );
}

export function OfferRequestChatCard({
    contact,
    leadStatus,
    contactRestricted,
    onRevealContactDetail,
    messages,
    isLoading,
    isLoadError,
    isSending,
    isSendError,
    isUploadingAttachments,
    uploadError,
    onDismissUploadError,
    hasOlderMessages,
    isLoadingOlderMessages,
    onLoadOlderMessages,
    pendingAttachments,
    onUploadFiles,
    onRemoveAttachment,
    onSendMessage,
    isInputDisabled = false,
    disabledNotice,
    isOnOldContract,
    fillHeight = false,
}: OfferRequestChatCardProps): ReactElement {
    const { t, i18n } = useTranslation();
    const [draft, setDraft] = useState('');
    const listRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const newestMessageUuid = messages.at(-1)?.uuid;

    // Keep the newest message in view when it changes
    useEffect(() => {
        const list = listRef.current;
        if (list && newestMessageUuid !== undefined) {
            list.scrollTop = list.scrollHeight;
        }
    }, [newestMessageUuid]);

    async function handleSend(): Promise<void> {
        const text = draft.trim();
        if ((!text.length && pendingAttachments.length === 0) || isSending) {
            return;
        }
        // Clear the draft only once the send succeeds, so a failed send keeps the message (mirrors the
        // customer chat). The failure itself surfaces via isSendError.
        const sent = await onSendMessage({
            text,
            attachmentUuids: pendingAttachments.map((attachment) => attachment.uuid),
        }).then(
            () => true,
            () => false,
        );
        if (sent) {
            setDraft('');
        }
    }

    function handleFileSelected(event: ChangeEvent<HTMLInputElement>): void {
        const files = Array.from(event.target.files ?? []);
        event.target.value = '';
        if (files.length > 0) {
            onUploadFiles(files);
        }
    }

    const canAddAttachments = pendingAttachments.length < MAX_ENGAGEMENT_CHAT_ATTACHMENTS && !isUploadingAttachments;

    return (
        <Card variant="outlined" sx={fillHeight ? FILL_HEIGHT_CARD_SX : undefined}>
            {contact ? (
                <>
                    <OfferRequestContactHeader
                        contact={contact}
                        leadStatus={leadStatus}
                        contactRestricted={contactRestricted}
                        isOnOldContract={isOnOldContract}
                        onRevealContactDetail={onRevealContactDetail}
                    />
                    <Divider />
                </>
            ) : (
                <CardHeader
                    title={t('OFFER_REQUESTS.CHAT.TITLE')}
                    slotProps={{ title: { variant: 'h6', component: 'h2' } }}
                />
            )}
            <CardContent
                sx={{
                    pt: contact ? 2 : 0,
                    ...(fillHeight ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}),
                }}>
                <Stack spacing={2} sx={fillHeight ? { flex: 1, minHeight: 0 } : undefined}>
                    {isLoadError ? (
                        <Alert severity="error">{t('OFFER_REQUESTS.CHAT.ERROR.LOAD')}</Alert>
                    ) : isLoading ? (
                        <Stack alignItems="center" sx={{ py: 4 }}>
                            <CircularProgress size={28} aria-label={t('OFFER_REQUESTS.CHAT.LOADING')} />
                        </Stack>
                    ) : (
                        <Box
                            ref={listRef}
                            sx={{
                                maxHeight: fillHeight ? 'none' : MESSAGE_LIST_MAX_HEIGHT,
                                ...(fillHeight ? { flex: 1, minHeight: 0 } : {}),
                                overflowY: 'auto',
                                pr: 1,
                            }}
                            aria-label={t('OFFER_REQUESTS.CHAT.MESSAGES')}>
                            <Stack spacing={2}>
                                {hasOlderMessages && (
                                    <Button
                                        onClick={onLoadOlderMessages}
                                        loading={isLoadingOlderMessages}
                                        size="small"
                                        sx={{ alignSelf: 'center' }}>
                                        {t('OFFER_REQUESTS.CHAT.LOAD_OLDER')}
                                    </Button>
                                )}
                                {messages.length === 0 && !isInputDisabled ? (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ py: 3, textAlign: 'center' }}>
                                        {t('OFFER_REQUESTS.CHAT.EMPTY')}
                                    </Typography>
                                ) : (
                                    <>
                                        {messages.map((message) =>
                                            message.sender.type === EngagementChatSenderType.System ? (
                                                <ChatSystemNotice key={message.uuid} message={message} />
                                            ) : (
                                                <ChatMessageBubble
                                                    key={message.uuid}
                                                    message={message}
                                                    locale={i18n.language}
                                                />
                                            ),
                                        )}
                                    </>
                                )}
                            </Stack>
                        </Box>
                    )}

                    {disabledNotice !== undefined && (
                        <ChatStatusAlert severity="warning">{disabledNotice}</ChatStatusAlert>
                    )}

                    {isSendError && <Alert severity="error">{t('OFFER_REQUESTS.CHAT.ERROR.SEND')}</Alert>}
                    <ChatErrorAlert message={uploadError} onDismiss={onDismissUploadError} />

                    <ChatPendingAttachments
                        attachments={pendingAttachments}
                        isUploading={isUploadingAttachments}
                        onRemove={onRemoveAttachment}
                    />

                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={ENGAGEMENT_CHAT_ATTACHMENT_ACCEPT}
                        hidden
                        onChange={handleFileSelected}
                    />

                    <Stack direction="row" spacing={1} alignItems="flex-end">
                        <TextField
                            value={draft}
                            onChange={(event) => setDraft(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' && !event.shiftKey) {
                                    event.preventDefault();
                                    void handleSend();
                                }
                            }}
                            placeholder={t('OFFER_REQUESTS.CHAT.INPUT_PLACEHOLDER')}
                            multiline
                            maxRows={5}
                            fullWidth
                            size="small"
                            disabled={isLoadError || isInputDisabled}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <IconButton
                                                size="small"
                                                aria-label={t('OFFER_REQUESTS.CHAT.ATTACH_FILE')}
                                                disabled={isLoadError || isInputDisabled || !canAddAttachments}
                                                onClick={() => fileInputRef.current?.click()}>
                                                {isUploadingAttachments ? (
                                                    <CircularProgress
                                                        size={18}
                                                        aria-label={t('OFFER_REQUESTS.CHAT.UPLOADING_ATTACHMENTS')}
                                                    />
                                                ) : (
                                                    <AttachFileOutlinedIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                        <IconButton
                            color="primary"
                            onClick={() => void handleSend()}
                            disabled={
                                isInputDisabled ||
                                (!draft.trim().length && pendingAttachments.length === 0) ||
                                isSending ||
                                isLoadError
                            }
                            aria-label={t('OFFER_REQUESTS.CHAT.SEND')}>
                            <SendIcon />
                        </IconButton>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}
